import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, CheckCircle2, RotateCcw, PlayCircle, Rocket, Swords, Zap } from "lucide-react";

/**
 * Mission Control visual path — a Duolingo-style serpentine trail connecting each day's
 * node, with boss-battle checkpoints centered on the spine. Pure CSS zigzag (alternating
 * left/right offsets around a central connecting line) — no SVG path math needed since
 * every node sits on a predictable vertical rhythm.
 */
function PathNode({ c, side, isCurrent, linkTo }) {
  const isLocked = c.status === "locked";
  const isCompleted = c.status === "completed";
  const isInProgress = c.status === "unlocked" && c.attempts > 0;
  const Wrapper = isLocked ? "div" : Link;

  const ringColor = isCompleted ? "border-emerald-400 bg-emerald-400/15 text-emerald-400"
    : isInProgress ? "border-indigo-400 bg-indigo-400/15 text-indigo-400"
    : isLocked ? "border-white/10 bg-white/[0.02] text-slate-500"
    : "border-indigo-400 bg-indigo-400/10 text-indigo-300";

  return (
    <div className={`flex items-center gap-4 ${side === "right" ? "flex-row-reverse" : ""}`} data-testid={`path-node-row-${c.day}`}>
      <div className={`flex-1 ${side === "right" ? "text-right" : ""}`}>
        <Wrapper to={linkTo} data-testid={`path-node-card-${c.day}`} className={`inline-block max-w-[240px] ${isLocked ? "cursor-not-allowed" : "group"}`}>
          <div className={`rounded-xl border px-4 py-3 transition-all ${isLocked ? "border-white/5 bg-white/[0.01] opacity-50" : "border-white/10 bg-white/[0.03] group-hover:border-indigo-400/40 group-hover:-translate-y-0.5"}`}>
            <p className={`text-sm font-medium truncate ${isLocked ? "text-slate-500" : "text-white"}`}>{c.title}</p>
            <div className={`flex items-center gap-2 mt-1.5 text-[10px] ${side === "right" ? "justify-end" : ""}`}>
              <span className="text-[#FBBF24] flex items-center gap-1"><Zap className="w-3 h-3" />{c.xp_reward} XP</span>
              {isCompleted && c.quiz_score != null && <span className="text-emerald-400 font-semibold">Score {c.quiz_score}%</span>}
            </div>
          </div>
        </Wrapper>
      </div>

      <div className="relative shrink-0 z-10">
        {isCurrent && (
          <motion.div
            animate={{ scale: [1, 1.35, 1], opacity: [0.6, 0, 0.6] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute inset-0 rounded-full bg-indigo-400"
          />
        )}
        <Wrapper
          to={linkTo} data-testid={`path-node-${c.day}`}
          className={`relative w-14 h-14 rounded-full border-2 flex items-center justify-center font-heading font-bold text-sm ${ringColor} ${isCurrent ? "ring-4 ring-indigo-400/30" : ""}`}
        >
          {isCurrent ? <Rocket className="w-5 h-5" />
            : isLocked ? <Lock className="w-4 h-4" />
            : isCompleted ? <CheckCircle2 className="w-5 h-5" />
            : isInProgress ? <RotateCcw className="w-5 h-5" />
            : <PlayCircle className="w-5 h-5" />}
        </Wrapper>
        <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] font-bold text-slate-500 whitespace-nowrap">DAY {c.day}</span>
      </div>

      <div className="flex-1" />
    </div>
  );
}

function BossNode({ day, checkpoint, onOpen }) {
  const unlocked = checkpoint?.unlocked;
  const completed = checkpoint?.completed;
  return (
    <div className="flex items-center justify-center" data-testid={`path-boss-row-${day}`}>
      <button
        onClick={() => unlocked && onOpen(day)}
        disabled={!unlocked}
        data-testid={`path-boss-node-${day}`}
        className={`relative z-10 w-16 h-16 rounded-2xl rotate-45 border-2 flex items-center justify-center ${
          !unlocked ? "border-white/10 bg-white/[0.02] opacity-50"
          : completed ? "border-emerald-400 bg-emerald-400/15"
          : "border-violet-400 bg-violet-400/15 shadow-[0_0_20px_rgba(139,92,246,0.35)]"
        }`}
      >
        <span className="-rotate-45">
          {!unlocked ? <Lock className="w-5 h-5 text-slate-500" /> : completed ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <Swords className="w-5 h-5 text-violet-300" />}
        </span>
      </button>
    </div>
  );
}

export default function ChallengePathMap({ weeks, checkpointByDay, checkpointDays = [], currentDay, onOpenBoss, dayLinkPrefix = "/challenge" }) {
  let flatIndex = 0;
  return (
    <div className="relative" data-testid="challenge-path-map">
      <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-indigo-500/40 via-violet-500/30 to-white/5 -translate-x-1/2 z-0" />
      <div className="relative space-y-10 py-4">
        {weeks.map((week) => (
          <div key={week.label}>
            <div className="flex justify-center mb-6">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-indigo-300 bg-[#0f1522] border border-indigo-400/30 rounded-full px-4 py-1.5" data-testid={`path-week-label-${week.label.replace(" ", "-").toLowerCase()}`}>
                {week.label} · {week.title}
              </span>
            </div>
            <div className="space-y-8">
              {week.challenges.map((c) => {
                const side = flatIndex % 2 === 0 ? "left" : "right";
                flatIndex += 1;
                return (
                  <React.Fragment key={c.day}>
                    <PathNode c={c} side={side} isCurrent={c.day === currentDay} linkTo={c.status === "locked" ? undefined : `${dayLinkPrefix}/${c.day}`} />
                    {checkpointDays.includes(c.day) && <BossNode day={c.day} checkpoint={checkpointByDay[c.day]} onOpen={onOpenBoss} />}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
